import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Eye,
  MessageCircle,
  ThumbsUp,
  Clock,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  Zap,
  Fish,
  Shield,
  AlertCircle as AlertIcon,
  Trash2
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { goToLogin } from '../utils/authRedirect'
import SEO from '../components/SEO'
import api from '../utils/api'

function PostDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, getLevelName } = useAuth()
  const [post, setPost] = useState(null)
  const [replies, setReplies] = useState([])
  const [loading, setLoading] = useState(true)
  const [replyContent, setReplyContent] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [liked, setLiked] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    fetchPostDetail()
  }, [id])

  const fetchPostDetail = async () => {
    setLoading(true)
    try {
      const response = await api.get(`/forum/posts/${id}`)
      if (response.data.success) {
        setPost(response.data.post)
        setReplies(response.data.replies || [])
      }
    } catch (error) {
      console.error('获取帖子详情失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleReply = async (e) => {
    e.preventDefault()

    if (!user) {
      goToLogin('/login')
      return
    }
    
    if (!replyContent.trim()) return

    setSubmitting(true)
    try {
      const response = await api.post(`/forum/posts/${id}/replies`, {
        content: replyContent.trim()
      })
      if (response.data.success) {
        setReplyContent('')
        fetchPostDetail()
      }
    } catch (error) {
      console.error('回复失败:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleLike = async () => {
    if (!user) {
      goToLogin('/login')
      return
    }

    try {
      const response = await api.post(`/forum/posts/${id}/like`)
      if (response.data.success) {
        setLiked(response.data.liked)
        setPost(prev => ({
          ...prev,
          likes: response.data.liked ? prev.likes + 1 : prev.likes - 1
        }))
      }
    } catch (error) {
      console.error('点赞失败:', error)
    }
  }

  const handleDelete = async () => {
    if (!user) return

    setDeleting(true)
    try {
      const response = await api.delete(`/forum/posts/${id}`)
      if (response.data.success) {
        navigate('/forum')
      }
    } catch (error) {
      console.error('删除失败:', error)
      alert(error.response?.data?.error || '删除失败')
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const isAuthor = user && post && user.id === post.user_id

  const getCategoryIcon = (slug) => {
    switch (slug) {
      case 'skill': return <Zap className="w-4 h-4" />
      case 'shrimp': return <Fish className="w-4 h-4" />
      case 'guide': return <Shield className="w-4 h-4" />
      case 'complaint': return <AlertIcon className="w-4 h-4" />
      default: return <MessageCircle className="w-4 h-4" />
    }
  }

  const getCategoryColor = (slug) => {
    switch (slug) {
      case 'skill': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
      case 'shrimp': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      case 'guide': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
      case 'complaint': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
    }
  }

  const getLevelColor = (level) => {
    const colors = {
      1: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      2: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      3: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      4: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      5: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      6: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    }
    return colors[level] || colors[1]
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-dclaw-600" />
      </div>
    )
  }

  if (!post) {
    return (
      <div className="text-center py-12 px-4">
        <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">帖子不存在或已被删除</p>
        <Link to="/forum" className="text-dclaw-600 hover:text-dclaw-700 mt-4 inline-block text-sm sm:text-base">
          返回讨论区
        </Link>
      </div>
    )
  }

  return (
    <article className="animate-in">
      <SEO 
        title={`${post.title} - 讨论区`}
        description={post.content.substring(0, 150) + '...'}
        keywords={`${post.category_name}, 论坛, DClaw讨论区, 小龙虾，openclaw，一键安装`}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/forum')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-dclaw-600 dark:hover:text-dclaw-400 mb-4 sm:mb-6 text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          返回讨论区
        </button>

        {/* Post */}
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-gray-200 dark:border-gray-700 mb-4 sm:mb-6">
          {/* Header */}
          <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="flex-shrink-0">
              {post.avatar ? (
                <img
                  src={post.avatar}
                  alt={post.nickname}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full"
                />
              ) : (
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-dclaw-400 to-dclaw-600 flex items-center justify-center text-white font-medium text-base sm:text-lg">
                  {post.nickname?.[0] || '?'}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-xs ${getLevelColor(post.level)}`}>
                  Lv{post.level} {getLevelName(post.level)}
                </span>
                <span className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">
                  {post.nickname}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTime(post.created_at)}
                </span>
                <span className={`flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full text-xs ${getCategoryColor(post.category_slug)}`}>
                  {getCategoryIcon(post.category_slug)}
                  {post.category_name}
                </span>
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
            {post.is_pinned === 1 && (
              <span className="inline-block px-1.5 sm:px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs sm:text-sm rounded-full mr-1.5 sm:mr-2">
                置顶
              </span>
            )}
            {post.is_essence === 1 && (
              <span className="inline-block px-1.5 sm:px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-xs sm:text-sm rounded-full mr-1.5 sm:mr-2">
                精华
              </span>
            )}
            {post.title}
          </h1>

          {/* Content */}
          <div className="prose dark:prose-invert max-w-none mb-4 sm:mb-6">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-sm sm:text-base">
              {post.content}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={handleLike}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base min-h-[44px] ${
                  liked
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <ThumbsUp className={`w-4 h-4 sm:w-5 sm:h-5 ${liked ? 'fill-current' : ''}`} />
                <span>{post.likes}</span>
              </button>
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-sm sm:text-base">
                <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>{post.views}</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-sm sm:text-base">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>{replies.length}</span>
              </div>
            </div>

            {/* 删除按钮 - 仅作者可见 */}
            {isAuthor && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-sm sm:text-base min-h-[44px]"
              >
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>删除</span>
              </button>
            )}
          </div>
        </div>

        {/* Replies */}
        <section className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
            回复 ({replies.length})
          </h3>
          
          {replies.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-xl sm:rounded-2xl">
              <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">暂无回复，来抢沙发吧！</p>
            </div>
          ) : (
            replies.map((reply) => (
              <div
                key={reply.id}
                className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-shrink-0">
                    {reply.avatar ? (
                      <img
                        src={reply.avatar}
                        alt={reply.nickname}
                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-dclaw-400 to-dclaw-600 flex items-center justify-center text-white font-medium text-sm sm:text-base">
                        {reply.nickname?.[0] || '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                      <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-xs ${getLevelColor(reply.level)}`}>
                        Lv{reply.level} {getLevelName(reply.level)}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">
                        {reply.nickname}
                      </span>
                      {reply.is_accepted === 1 && (
                        <span className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          已采纳
                        </span>
                      )}
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-1.5 sm:mb-2 text-sm sm:text-base">
                      {reply.content}
                    </p>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTime(reply.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </section>

        {/* Reply Form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
            发表回复
          </h3>
          
          {user ? (
            <form onSubmit={handleReply}>
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="写下你的回复..."
                rows={3}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-dclaw-500 dark:text-white resize-none mb-3 sm:mb-4 text-sm sm:text-base"
              />
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting || !replyContent.trim()}
                  className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-dclaw-600 hover:bg-dclaw-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-xl font-medium transition-colors text-sm sm:text-base min-h-[44px]"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      发送中...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                      发表回复
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center py-6 sm:py-8">
              <p className="text-gray-500 dark:text-gray-400 mb-4 text-sm sm:text-base">
                登录后才能发表回复
              </p>
              <button
                onClick={() => goToLogin('/login')}
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-dclaw-600 hover:bg-dclaw-700 text-white rounded-xl font-medium transition-colors text-sm sm:text-base min-h-[44px]"
              >
                立即登录
              </button>
            </div>
          )}
        </div>

        {/* 删除确认弹窗 */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-5 sm:p-6 max-w-sm w-full mx-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                确认删除
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-5 sm:mb-6 text-sm sm:text-base">
                确定要删除这个帖子吗？删除后无法恢复。
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 sm:px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm sm:text-base min-h-[44px]"
                >
                  取消
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors text-sm sm:text-base min-h-[44px]"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      删除中...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      确认删除
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </article>
  )
}

export default PostDetail
